import React, { useEffect, useState } from "react";
import StudioBrand from "./StudioBrand";
import {
  completePasswordRecovery,
  createPasswordRecoveryRequest,
  fetchStudioSummary,
  login,
} from "../api/client";
import { writeFlag } from "../hooks/usePortal";
import { ENTER_FLAG } from "./portal/EntranceFlash";
import { resolveLoginReturnPath } from "../authRouting";
import { resolveBranding } from "../presentationBranding";

const LOGIN_API = {
  completePasswordRecovery,
  createPasswordRecoveryRequest,
  fetchStudioSummary,
  login,
};

export default function LoginScreen({ api = LOGIN_API }) {
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState(() => resolveBranding());

  useEffect(() => {
    let active = true;
    api.fetchStudioSummary().then((summary) => {
      if (active) setBranding(resolveBranding(summary?.branding));
    });
    return () => { active = false; };
  }, [api]);

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setNotice("");
    if (nextMode !== "login") setPassword("");
    if (mode === "complete" && nextMode !== "complete") {
      setToken("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.login(username, password);
      writeFlag(ENTER_FLAG, true);
      window.location.href = resolveLoginReturnPath(window.location.search, window.location.origin);
    } catch (err) {
      setError(err.message || "Invalid credentials.");
    } finally {
      setBusy(false);
    }
  };

  const handleRequest = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await api.createPasswordRecoveryRequest(username.trim());
      setNotice(result.message || "If eligible, the request will be reviewed by a studio administrator.");
    } catch (err) {
      setError(err.message || "Password recovery is unavailable.");
    } finally {
      setBusy(false);
    }
  };

  const handleComplete = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (newPassword !== confirmPassword) {
      setError("Passwords must match.");
      return;
    }
    setBusy(true);
    try {
      await api.completePasswordRecovery({
        username: username.trim(),
        token: token.trim(),
        newPassword,
      });
      setPassword("");
      setToken("");
      setNewPassword("");
      setConfirmPassword("");
      setNotice("Password reset completed. Sign in with your new password.");
      setMode("login");
    } catch (err) {
      setError(err.message || "Recovery code or request is invalid or unavailable.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-screen">
      <section className="login-card" aria-labelledby="login-title">
        {branding.deployment.logo ? (
          <>
            <StudioBrand branding={branding} compact />
            <h1 id="login-title" className="visually-hidden">{branding.deployment.name || "Studio"}</h1>
          </>
        ) : (
          <>
            <StudioBrand branding={branding} bannerClassName="login-banner" />
            <div className="login-platform">{branding.platform.name}</div>
            <h1 id="login-title" className="login-title">{branding.deployment.name || "Studio"}</h1>
            {branding.deployment.descriptor && <p className="login-copy">{branding.deployment.descriptor}</p>}
            {branding.deployment.tagline && <p className="login-copy">{branding.deployment.tagline}</p>}
            {branding.attribution.text && <p className="login-copy">{branding.attribution.text}</p>}
          </>
        )}

        {mode === "login" && (
          <form onSubmit={handleLogin}>
            <p className="login-copy">Welcome back. Sign in to step inside.</p>
            <label className="login-label" htmlFor="login-username">Email or username</label>
            <input id="login-username" className="login-input" value={username}
              autoComplete="username" onChange={(event) => setUsername(event.target.value)} />
            <label className="login-label" htmlFor="login-password">Password</label>
            <input id="login-password" className="login-input" type="password" value={password}
              autoComplete="current-password" onChange={(event) => setPassword(event.target.value)} />
            {notice && <div className="login-notice" role="status">{notice}</div>}
            {error && <div className="login-error" role="alert">{error}</div>}
            <button className="login-submit" type="submit" disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <div className="login-actions">
              <button className="login-link" type="button" onClick={() => changeMode("request")}>Forgot password?</button>
            </div>
            <p className="login-copy login-join">New here? <a href="/#join">Join the family</a></p>
          </form>
        )}

        {mode === "request" && (
          <form onSubmit={handleRequest}>
            <h2 className="login-subtitle">Request password recovery</h2>
            <p className="login-copy">
              Submit a request for administrator review. This site does not send recovery email.
              If approved, collect a short-lived code directly from an administrator in person
              after they verify your identity.
            </p>
            <label className="login-label" htmlFor="recovery-username">HACaMS username</label>
            <input id="recovery-username" className="login-input" value={username}
              autoComplete="username" onChange={(event) => setUsername(event.target.value)} />
            {notice && <div className="login-notice" role="status">{notice}</div>}
            {error && <div className="login-error" role="alert">{error}</div>}
            <button className="login-submit" type="submit" disabled={busy}>
              {busy ? "Submitting…" : "Submit request"}
            </button>
            <div className="login-actions">
              <button className="login-link" type="button" onClick={() => changeMode("complete")}>I have a recovery code</button>
              <button className="login-link" type="button" onClick={() => changeMode("login")}>Back to sign in</button>
            </div>
          </form>
        )}

        {mode === "complete" && (
          <form onSubmit={handleComplete}>
            <h2 className="login-subtitle">Complete password reset</h2>
            <p className="login-copy">Enter the code given to you directly by an administrator. It expires after 20 minutes and works once.</p>
            <label className="login-label" htmlFor="recovery-complete-username">HACaMS username</label>
            <input id="recovery-complete-username" className="login-input" value={username}
              autoComplete="username" onChange={(event) => setUsername(event.target.value)} />
            <label className="login-label" htmlFor="recovery-token">Recovery code</label>
            <input id="recovery-token" className="login-input" value={token}
              autoComplete="one-time-code" onChange={(event) => setToken(event.target.value)} />
            <label className="login-label" htmlFor="recovery-new-password">New password</label>
            <input id="recovery-new-password" className="login-input" type="password" value={newPassword}
              autoComplete="new-password" onChange={(event) => setNewPassword(event.target.value)} />
            <label className="login-label" htmlFor="recovery-confirm-password">Confirm new password</label>
            <input id="recovery-confirm-password" className="login-input" type="password" value={confirmPassword}
              autoComplete="new-password" onChange={(event) => setConfirmPassword(event.target.value)} />
            {error && <div className="login-error" role="alert">{error}</div>}
            <button className="login-submit" type="submit" disabled={busy}>
              {busy ? "Resetting…" : "Reset password"}
            </button>
            <div className="login-actions">
              <button className="login-link" type="button" onClick={() => changeMode("request")}>Request a code</button>
              <button className="login-link" type="button" onClick={() => changeMode("login")}>Back to sign in</button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
